import React, { useState, useCallback } from 'react';
import { X, Trash2 } from 'lucide-react';
import { useLazyCommand, useDomainEvent, useEvent } from '@soap/modules';

interface DeleteAssessmentDialogProps {
    unitId: string;
    assessmentTitle?: string;
    onConfirm: () => void;
    onClose: () => void;
}

const viewStates = {
    ready: 0,
    submitting: 1
};

export function DeleteAssessmentDialog({ unitId, assessmentTitle, onConfirm, onClose }: DeleteAssessmentDialogProps) {
    const [viewState, setViewState] = useState(viewStates.ready);
    const [sendCancelAssessmentCommand] = useLazyCommand('C211v1_CancelAssessment');
    const triggerCloseContextMenu = useDomainEvent({
        eventName: "close-context-menu",
    });

    useEvent({
        eventName: 'E169v1_AssessmentCancelled',
        onEventReceived: (event: any) => {
            if (event.e169_UnitId === unitId) {
                handleClose();
            }
        }
    });

    const handleClose = useCallback(() => {
        setViewState(viewStates.ready);
        onClose();
        triggerCloseContextMenu();
    }, [onClose, triggerCloseContextMenu]);

    const handleDelete = async () => {
        setViewState(viewStates.submitting);
        
        // Notify parent to set loading state
        onConfirm();

        try {
            await sendCancelAssessmentCommand({
                C211_UnitId: unitId
            });
            // Dialog will close when E169v1_AssessmentCancelled event is received
        } catch (error) {
            console.error('Error deleting assessment:', error);
            setViewState(viewStates.ready);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Delete Assessment</h2>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
                            <p className="text-sm text-destructive-action-dark">
                                This action cannot be undone.
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                Are you sure you want to delete this assessment {assessmentTitle ? <strong>{assessmentTitle}</strong> : ''}?
                            </p>
                            <p className="text-sm text-destructive-action-dark mt-2">
                                This will permanently delete the assessment and all associated data.
                            </p>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <h4 className="font-medium text-yellow-900 mb-2 flex items-center">
                                <Trash2 size={16} className="mr-2" />
                                What will be deleted:
                            </h4>
                            <ul className="ml-6 space-y-1 list-disc">
                                <li className="text-sm text-yellow-800">The assessment itself</li>
                                <li className="text-sm text-yellow-800">All assessment results permanently, including from students' assessment history</li>
                                <li className="text-sm text-yellow-800">All shared assessment links</li>
                                <li className="text-sm text-yellow-800">All progress and data related to this assessment</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-6">
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            disabled={viewState === viewStates.submitting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            disabled={viewState === viewStates.submitting}
                        >
                            <Trash2 size={16} className={`mr-2 ${viewState === viewStates.submitting ? 'animate-spin' : ''}`} />
                            {viewState === viewStates.submitting ? 'Deleting...' : 'Delete Assessment'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

